import { useState } from 'react';
import { useUserActivity } from '../hooks';
import { TableSkeleton } from '../components/LoadingSkeleton';
import { EmptyState, ErrorState } from '../components/EmptyState';

export function ActivityTab({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');
  const { data, isLoading, isError, error, refetch } = useUserActivity(userId, { page, limit: 30, action: action || undefined });

  if (isLoading) return <TableSkeleton rows={5} cols={3} />;
  if (isError) return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  if (!data || data.items.length === 0)
    return <EmptyState title="No activity yet" hint="This user has no recorded events." />;

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center">
        <input
          value={action}
          onChange={(e) => { setAction(e.target.value); setPage(1); }}
          placeholder="Filter by action (e.g. LOGIN)"
          className="px-3 py-1.5 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm"
        />
      </div>
      <ul className="space-y-2">
        {data.items.map((row) => (
          <li key={row.id} className="glass-panel rounded-lg p-3 flex justify-between gap-4">
            <div>
              <div className="font-bold text-sm">{row.action}</div>
              <div className="text-[11px] text-on-surface-variant mt-1 break-all">
                {row.metadataJson !== '{}' ? row.metadataJson : ''}
              </div>
            </div>
            <div className="text-right text-[11px] text-on-surface-variant whitespace-nowrap">
              {new Date(row.createdAt).toLocaleString('en-IN')}
              {row.ipAddress && <div>{row.ipAddress}</div>}
            </div>
          </li>
        ))}
      </ul>
      <Pager page={page} totalPages={data.meta.totalPages} onChange={setPage} />
    </div>
  );
}

function Pager({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (n: number) => void }) {
  return (
    <div className="flex justify-end gap-2 text-xs">
      <button
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="px-3 py-1.5 rounded-md border border-border-subtle disabled:opacity-40"
      >
        Prev
      </button>
      <span className="px-2 py-1.5 text-on-surface-variant">
        Page {page} / {totalPages || 1}
      </span>
      <button
        disabled={page >= (totalPages || 1)}
        onClick={() => onChange(page + 1)}
        className="px-3 py-1.5 rounded-md border border-border-subtle disabled:opacity-40"
      >
        Next
      </button>
    </div>
  );
}
